import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import UserService from "src/Services/API/user.service";
import BranchService from "src/Services/API/branch.service";
import Loader from "src/Components/Common/Loader";
import { getAvatarUrl } from "src/Common/common";
import { can, handleError, isSuperAdminUser } from "src/Common/function";

import PayRulesDropdown from "./PayRulesDropdown";
import {
  OT_DAILY_8,
  OT_WEEKLY_40,
  PAY_RULE_FIELDS,
  effectivePayRules,
  hasOvertimeRule,
  hasOwnPayRule,
} from "./hours";

const MODE_INDIVIDUALS = "individuals";
const MODE_BRANCHES = "branches";

// Real avatar when there is one, initials circle otherwise. Same treatment as
// the Team member list so a person looks the same in both places.
function MemberAvatar({ user }) {
  const [errored, setErrored] = useState(false);
  if (user?.avatar && !errored) {
    return (
      <img
        src={getAvatarUrl(user.avatar)}
        onError={() => setErrored(true)}
        alt={user?.name ?? ""}
        className="shrink-0 w-9 h-9 rounded-full object-cover"
      />
    );
  }
  const initial = (user?.name || user?.email || "?")[0].toUpperCase();
  return (
    <span className="shrink-0 w-9 h-9 rounded-full bg-[#EAF3FF] text-[#0058E5] text-[14px] font-OpenSansSemiBold flex items-center justify-center">
      {initial}
    </span>
  );
}

/**
 * Compact single-select used for the Role and Branch filters.
 *
 * Written out rather than using a native <select> for the same reason
 * DateRangeSelector's preset list is: a browser-styled select sits
 * conspicuously differently next to the search input beside it.
 *
 * Not portaled, unlike the rule dropdown — these live in the drawer header,
 * which is outside the scrolling list and clips nothing.
 */
function FilterSelect({ label, value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const current = options.find((option) => option.value === value);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const active = value !== null;

  return (
    <div ref={wrapRef} className="relative flex-1 min-w-0">
      <button
        type="button"
        onClick={() => setOpen((previous) => !previous)}
        aria-expanded={open}
        aria-haspopup="listbox"
        title={current ? `${label}: ${current.label}` : label}
        className={`w-full h-10 px-3 rounded-[6px] outline outline-1 -outline-offset-1 flex items-center justify-between gap-2 text-[14px] leading-5 tracking-[0.14px] ${
          active
            ? "outline-[#0065FD] bg-[#EAF3FF] text-[#0058E5] font-OpenSansSemiBold"
            : "outline-[#E5E8F3] bg-white text-[#1A2332] font-OpenSansRegular hover:bg-[#F7F9FC]"
        }`}
      >
        <span className="truncate">{current ? current.label : `All ${label}`}</span>
        <svg width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true" className="shrink-0">
          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open ? (
        <div
          role="listbox"
          className="absolute z-30 mt-1 w-full max-h-[240px] overflow-y-auto rounded-[8px] border border-[#E5E8F3] bg-white py-1 shadow-[0px_12px_32px_rgba(16,24,40,0.16)]"
        >
          {[{ value: null, label: `All ${label}` }, ...options].map((option) => (
            <button
              key={option.value ?? "all"}
              type="button"
              role="option"
              aria-selected={option.value === value}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
              className={`w-full px-3 py-1.5 text-left text-[13px] leading-[18px] truncate ${
                option.value === value
                  ? "bg-[#EAF3FF] text-[#0058E5] font-OpenSansSemiBold"
                  : "text-[#45556C] font-OpenSansRegular hover:bg-[#F7F9FC]"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Assign each team member the overtime rule they are paid under.
 *
 * Opened from a button on Time Management rather than living in Team → Manage:
 * this is a payroll classification, and the person doing it is working in the
 * payroll screen, not administering the team.
 *
 * Deliberately shows an explicit "Unassigned" state instead of defaulting
 * everyone to a rule. An unassigned member is one payroll has not classified,
 * and the overtime derivation is meant to decline to compute for them — a
 * default would turn a missing decision into a confident wrong paycheck.
 */
const OtRuleDrawer = ({ isOpen, onClose, branch = 0 }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  // user.id -> true while that row's save is in flight, so one slow row does
  // not lock the whole list.
  const [savingIds, setSavingIds] = useState({});
  const [query, setQuery] = useState("");
  // null means "all". Held as ids rather than names so two roles or branches
  // that share a name cannot collapse into one filter.
  const [roleFilter, setRoleFilter] = useState(null);
  const [branchFilter, setBranchFilter] = useState(null);
  // Rules can be set per branch — the default for everyone on it — or per
  // person, which overrides the branch flag by flag. Two views of one policy.
  const [mode, setMode] = useState(MODE_INDIVIDUALS);
  // Every branch with its default rules. Loaded whichever view is open,
  // because the individual view needs it to show what someone inherits.
  const [branchRules, setBranchRules] = useState([]);
  const [savingBranchIds, setSavingBranchIds] = useState({});
  // Rules for a role within a branch — "all managers in Hawaii". Each is
  // { branchId, roleId, roleName, ...flags }. A rule being added but not yet
  // given a flag lives here too, with every flag null, until its first click
  // creates it server-side.
  const [roleRules, setRoleRules] = useState([]);
  const [savingRoleKeys, setSavingRoleKeys] = useState({});

  // can() resolves the *:* and user:* wildcards, so Administrator and Super
  // Admin pass without the key being granted explicitly.
  const editable = can("user:assignOtRule");

  // Double rAF so the panel transitions in on first open — matches
  // EditMemberDrawer, where a single rAF fires before the first paint.
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!isOpen) {
      setVisible(false);
      return undefined;
    }
    let secondId;
    const firstId = requestAnimationFrame(() => {
      secondId = requestAnimationFrame(() => setVisible(true));
    });
    return () => {
      cancelAnimationFrame(firstId);
      if (secondId) cancelAnimationFrame(secondId);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    BranchService.getAll()
      .then((response) => setBranchRules(Array.isArray(response.data) ? response.data : []))
      .catch((error) => handleError(error));
    BranchService.getRolePayRules()
      .then((response) => setRoleRules(Array.isArray(response.data) ? response.data : []))
      .catch((error) => handleError(error));
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    UserService.getAll(branch)
      .then((response) => {
        const data = Array.isArray(response.data) ? response.data : [];
        // Two kinds of member are dropped before anything else sees the list,
        // because neither can ever produce hours to classify:
        //
        // - Archived members cannot log hours, so they cannot generate overtime.
        // - Super Admins are the cross-tenant platform role and do not record
        //   time in the app at all. Left in, each one would sit here as
        //   permanently unassigned and pad the warning count with people nobody
        //   is ever going to classify.
        //
        // Doing it here rather than in the filters is what also keeps "Super
        // Admin" out of the Role dropdown: its options are built from the
        // members actually loaded, so a role nobody in the list holds is not
        // offered.
        const assignable = data.filter(
          (user) => user.isEnabled === true && !isSuperAdminUser(user)
        );
        assignable.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setUsers(assignable);
      })
      .catch((error) => handleError(error))
      .finally(() => setLoading(false));
  }, [isOpen, branch]);

  /**
   * Save one change to a user's payroll classification — the rule itself, or
   * either double-time premium.
   *
   * The rule and the premiums go through one endpoint because they are one
   * classification; sending only the changed key keeps the server from having
   * to guess whether an absent field means "clear it" or "leave it".
   */
  const patchUser = (user, patch) => {
    const changed = Object.keys(patch).filter((key) => (user[key] ?? null) !== patch[key]);
    if (!changed.length) return;
    const previous = Object.fromEntries(changed.map((key) => [key, user[key] ?? null]));

    // Optimistic: the control reflects the click immediately and rolls back if
    // the request fails, so a slow network does not read as an unresponsive
    // button and invite a second click.
    setUsers((prev) => prev.map((item) => (item.id === user.id ? { ...item, ...patch } : item)));
    setSavingIds((prev) => ({ ...prev, [user.id]: true }));

    UserService.assignOtRule(user.id, patch)
      .catch((error) => {
        setUsers((prev) => prev.map((item) => (item.id === user.id ? { ...item, ...previous } : item)));
        handleError(error);
      })
      .finally(() => {
        setSavingIds((prev) => {
          const next = { ...prev };
          delete next[user.id];
          return next;
        });
      });
  };

  /**
   * The branch whose default rules a person inherits: their current branch,
   * else the first they belong to — the same resolution the server uses.
   * Looked up in the loaded list rather than read off the user, so a rule
   * just changed in the branch view is what the individual view shows.
   */
  const branchOf = (user) => {
    const id = user?.currentBranch?.id ?? user?.branches?.[0]?.id ?? null;
    return id == null ? null : (branchRules.find((item) => item.id === id) ?? null);
  };

  // The role rules that apply to a person: their branch's rules for the roles
  // they hold, in the order the roles are held.
  const roleRulesOf = (user) => {
    const branchId = branchOf(user)?.id;
    if (branchId == null) return [];
    return (user?.roles || [])
      .map((role) => roleRules.find((rule) => rule.branchId === branchId && rule.roleId === role?.id))
      .filter(Boolean);
  };

  const effectiveOf = (user) => effectivePayRules(user, ...roleRulesOf(user), branchOf(user));

  // What a person without a rule of their own is following, for the subline.
  const followsLabel = (user) => {
    const branchItem = branchOf(user);
    if (!branchItem) return "No branch";
    const viaRole = roleRulesOf(user).find((rule) => PAY_RULE_FIELDS.some((field) => rule[field] != null));
    return viaRole ? `Follows ${branchItem.name} · ${viaRole.roleName}` : `Follows ${branchItem.name}`;
  };

  const roleKey = (branchId, roleId) => `${branchId}|${roleId}`;

  /**
   * Save one change to a (branch, role) rule. The server removes a rule whose
   * flags are all null, and the list here does the same, so a rule that was
   * only ever added and never set simply goes.
   */
  const patchRoleRule = (rule, patch) => {
    const key = roleKey(rule.branchId, rule.roleId);
    const previous = Object.fromEntries(PAY_RULE_FIELDS.map((field) => [field, rule[field] ?? null]));
    const next = { ...previous, ...patch };
    const saysNothing = PAY_RULE_FIELDS.every((field) => next[field] == null);

    setRoleRules((prev) =>
      saysNothing
        ? prev.filter((item) => roleKey(item.branchId, item.roleId) !== key)
        : prev.map((item) => (roleKey(item.branchId, item.roleId) === key ? { ...item, ...next } : item))
    );
    setSavingRoleKeys((prev) => ({ ...prev, [key]: true }));

    BranchService.assignRoleOtRule(rule.branchId, rule.roleId, patch)
      .catch((error) => {
        setRoleRules((prev) => {
          const present = prev.some((item) => roleKey(item.branchId, item.roleId) === key);
          return present
            ? prev.map((item) => (roleKey(item.branchId, item.roleId) === key ? { ...item, ...previous } : item))
            : [...prev, { ...rule, ...previous }];
        });
        handleError(error);
      })
      .finally(() => {
        setSavingRoleKeys((prev) => {
          const copy = { ...prev };
          delete copy[key];
          return copy;
        });
      });
  };

  // A new role rule starts empty on screen; its first flag creates it.
  const addRoleRule = (branchItem, roleId) => {
    const role = roleOptions.find((option) => option.value === roleId);
    if (!role) return;
    setRoleRules((prev) => [
      ...prev,
      {
        branchId: branchItem.id,
        roleId,
        roleName: role.label,
        ...Object.fromEntries(PAY_RULE_FIELDS.map((field) => [field, null])),
      },
    ]);
  };

  // Clearing a role rule is the same as removing it: a rule saying nothing
  // is not kept.
  const removeRoleRule = (rule) =>
    patchRoleRule(rule, Object.fromEntries(PAY_RULE_FIELDS.map((field) => [field, null])));

  // Back to following the branch: every own flag cleared in one patch.
  const followBranch = (user) =>
    patchUser(user, Object.fromEntries(PAY_RULE_FIELDS.map((field) => [field, null])));

  /** Save one change to a branch's default rules. Optimistic, rolled back on failure. */
  const patchBranch = (branchItem, patch) => {
    const changed = Object.keys(patch).filter((key) => (branchItem[key] ?? null) !== patch[key]);
    if (!changed.length) return;
    const previous = Object.fromEntries(changed.map((key) => [key, branchItem[key] ?? null]));

    setBranchRules((prev) =>
      prev.map((item) => (item.id === branchItem.id ? { ...item, ...patch } : item))
    );
    setSavingBranchIds((prev) => ({ ...prev, [branchItem.id]: true }));

    BranchService.assignOtRule(branchItem.id, patch)
      .catch((error) => {
        setBranchRules((prev) =>
          prev.map((item) => (item.id === branchItem.id ? { ...item, ...previous } : item))
        );
        handleError(error);
      })
      .finally(() => {
        setSavingBranchIds((prev) => {
          const next = { ...prev };
          delete next[branchItem.id];
          return next;
        });
      });
  };

  /**
   * Filter options built from the members actually loaded, not from the full
   * role and branch lists.
   *
   * Offering a filter that matches nobody is a dead end — the person picks it,
   * gets an empty list, and cannot tell whether they filtered wrongly or the
   * data is missing. Both are also already on each user from getAll, so this
   * costs no extra request.
   */
  const roleOptions = useMemo(() => {
    const byId = new Map();
    for (const user of users) {
      for (const role of user.roles || []) {
        if (role?.id != null && !byId.has(role.id)) byId.set(role.id, role.name || "Unnamed role");
      }
    }
    return [...byId.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [users]);

  const branchOptions = useMemo(() => {
    const byId = new Map();
    for (const user of users) {
      for (const branchItem of user.branches || []) {
        if (branchItem?.id != null && !byId.has(branchItem.id)) {
          byId.set(branchItem.id, branchItem.name || "Unnamed branch");
        }
      }
    }
    return [...byId.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [users]);

  // A filter whose option vanished — the list reloaded for a different branch,
  // say — would otherwise stay applied invisibly and hide everyone.
  useEffect(() => {
    if (roleFilter !== null && !roleOptions.some((option) => option.value === roleFilter)) {
      setRoleFilter(null);
    }
  }, [roleOptions, roleFilter]);

  useEffect(() => {
    if (branchFilter !== null && !branchOptions.some((option) => option.value === branchFilter)) {
      setBranchFilter(null);
    }
  }, [branchOptions, branchFilter]);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    return users.filter((user) => {
      if (roleFilter !== null && !(user.roles || []).some((role) => role?.id === roleFilter)) {
        return false;
      }
      if (
        branchFilter !== null &&
        !(user.branches || []).some((branchItem) => branchItem?.id === branchFilter)
      ) {
        return false;
      }
      if (!term) return true;
      return (
        user.name?.toLowerCase().includes(term) ||
        user.email?.toLowerCase().includes(term) ||
        (user.roles || []).some((role) => role.name?.toLowerCase().includes(term))
      );
    });
  }, [users, query, roleFilter, branchFilter]);

  const filtersApplied = roleFilter !== null || branchFilter !== null || query.trim() !== "";

  const clearFilters = () => {
    setQuery("");
    setRoleFilter(null);
    setBranchFilter(null);
  };

  // Counted on what each person is actually measured under — own flags over
  // the branch's — so someone covered by a branch rule is not called
  // unassigned.
  const unassignedCount = useMemo(
    () => users.filter((user) => !hasOvertimeRule(effectiveOf(user))).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [users, branchRules, roleRules],
  );

  // Daily without weekly is legitimate policy, but it is also what an oversight
  // looks like: six 8-hour days is 48 hours and no overtime, and the screen
  // would show that without a murmur. Counted so the drawer can say it out loud
  // rather than leaving it to be discovered on a paycheck.
  const dailyWithoutWeeklyCount = useMemo(
    () =>
      users.filter((user) => {
        const rules = effectiveOf(user);
        return rules[OT_DAILY_8] === true && rules[OT_WEEKLY_40] !== true;
      }).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [users, branchRules, roleRules],
  );

  if (!isOpen && !visible) return null;

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black bg-opacity-20 transition-opacity duration-300 ease-in-out ${
          visible ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed top-0 right-0 bottom-0 z-50 flex h-screen w-[560px] max-w-full transform flex-col bg-white shadow-[0_4px_24px_rgba(0,0,0,0.12)] transition-transform duration-300 ease-in-out ${
          visible ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="px-6 pt-5 pb-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[18px] leading-6 font-OpenSansSemiBold text-[#1A2332]">Overtime Policies</p>
            <p className="mt-1 text-[13px] leading-[18px] font-OpenSansRegular text-[#868DA6]">
              Set the policies per branch, and override them for any individual.
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-[#868DA6] hover:bg-[#F0F1F5] hover:text-[#1A2332]"
            title="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M12 4L4 12M4 4l8 8"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <div className="px-6 pb-3 flex flex-col gap-3">
          {/* Two views of one policy: a branch's rules are the default for
              everyone on it, and a person's own flags override them. */}
          <div className="flex rounded-[6px] outline outline-1 -outline-offset-1 outline-[#E5E8F3] p-[2px] bg-[#F7F9FC]">
            {[
              { value: MODE_INDIVIDUALS, label: "By individual" },
              { value: MODE_BRANCHES, label: "By branch" },
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setMode(option.value)}
                aria-pressed={mode === option.value}
                className={`flex-1 h-8 rounded-[5px] text-[13px] leading-[18px] ${
                  mode === option.value
                    ? "bg-white text-[#0058E5] font-OpenSansSemiBold shadow-[0_1px_2px_rgba(16,24,40,0.08)]"
                    : "text-[#5F6477] font-OpenSansRegular hover:text-[#1A2332]"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          {mode === MODE_BRANCHES ? (
            <p className="text-[12px] leading-[18px] font-OpenSansRegular text-[#868DA6]">
              A branch's policies apply to everyone on it who has no policy of their own. A role
              policy narrows that to one role within the branch — all managers in Hawaii, say —
              and a person's own policy, set in the individual view, overrides both.
            </p>
          ) : null}
          {mode === MODE_INDIVIDUALS ? (
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by name, email or role..."
            className="w-full h-10 px-3 rounded-[6px] outline outline-1 -outline-offset-1 outline-[#E5E8F3] text-[14px] leading-5 tracking-[0.14px] font-OpenSansRegular text-[#1A2332] placeholder:text-[#868DA6]"
          />
          ) : null}

          {/* Each filter is hidden when there is nothing to choose between —
              a single-branch tenant gains nothing from a branch filter that can
              only ever be "all". */}
          {mode === MODE_INDIVIDUALS && (roleOptions.length > 1 || branchOptions.length > 1) ? (
            <div className="flex items-center gap-2">
              {roleOptions.length > 1 ? (
                <FilterSelect label="roles" value={roleFilter} options={roleOptions} onChange={setRoleFilter} />
              ) : null}
              {branchOptions.length > 1 ? (
                <FilterSelect
                  label="branches"
                  value={branchFilter}
                  options={branchOptions}
                  onChange={setBranchFilter}
                />
              ) : null}
            </div>
          ) : null}

          {/* Says what is being hidden. A filtered list that looks like the whole
              team is how someone concludes a member is missing from the system. */}
          {mode === MODE_INDIVIDUALS && !loading && filtersApplied ? (
            <div className="flex items-center justify-between gap-3 text-[12px] leading-[18px] font-OpenSansRegular text-[#868DA6]">
              <span>
                Showing {filtered.length} of {users.length} members
              </span>
              <button
                type="button"
                onClick={clearFilters}
                className="shrink-0 font-OpenSansSemiBold text-[#0058E5] hover:underline"
              >
                Clear filters
              </button>
            </div>
          ) : null}
          {mode === MODE_INDIVIDUALS && !loading && dailyWithoutWeeklyCount > 0 ? (
            <div className="rounded-[6px] border border-[#E5E8F3] bg-[#F7F9FC] px-3 py-2 text-[12px] leading-[18px] font-OpenSansRegular text-[#5F6477]">
              {dailyWithoutWeeklyCount}{" "}
              {dailyWithoutWeeklyCount === 1 ? "member earns" : "members earn"} overtime over 8 hrs/day
              but not over 40 hrs/wk. Six 8-hour days would be 48 hours with no overtime — deliberate for
              some, worth a second look if not.
            </div>
          ) : null}
          {mode === MODE_INDIVIDUALS && !loading && unassignedCount > 0 ? (
            <div className="rounded-[6px] border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2 text-[12px] leading-[18px] font-OpenSansRegular text-[#92400E]">
              {unassignedCount} {unassignedCount === 1 ? "member has" : "members have"} no rule, their own or
              their branch's. Overtime will not be calculated for them until one is set.
            </div>
          ) : null}
        </div>

        <div className="flex-1 overflow-y-auto bg-[#FBFCFF] border-t border-[#E5E8F3] px-4 py-3">
          {mode === MODE_BRANCHES ? (
            branchRules.length === 0 ? (
              <p className="py-10 text-center text-[14px] font-OpenSansRegular text-[#868DA6]">
                No branches found.
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {branchRules.map((branchItem) => {
                  const saving = Boolean(savingBranchIds[branchItem.id]);
                  const following = users.filter(
                    (user) => branchOf(user)?.id === branchItem.id && !hasOwnPayRule(user)
                  ).length;
                  return (
                    <div
                      key={branchItem.id}
                      className="bg-white rounded-[8px] border border-[#E5E8F3] px-3 py-2.5"
                    >
                      <div className="flex items-center gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-[14px] leading-5 font-OpenSansSemiBold text-[#1A2332] truncate">
                            {branchItem.name || "Unnamed branch"}
                          </p>
                          <p className="text-[12px] leading-4 font-OpenSansRegular text-[#868DA6]">
                            {following === 0
                              ? "Nobody follows this branch's policies"
                              : `${following} ${following === 1 ? "member follows" : "members follow"} these policies`}
                          </p>
                        </div>
                        <div className="shrink-0 flex items-center gap-2">
                          {saving ? <Loader variant="inline" ariaLabel="saving" /> : null}
                          <PayRulesDropdown
                            user={branchItem}
                            saving={saving}
                            disabled={!editable}
                            onToggle={(patch) => patchBranch(branchItem, patch)}
                          />
                        </div>
                      </div>

                      {/* Rules for one role within this branch. Each refines
                          the branch's default for the people holding that
                          role; a person's own rule still wins over it. */}
                      {(() => {
                        const rulesHere = roleRules.filter((rule) => rule.branchId === branchItem.id);
                        const taken = new Set(rulesHere.map((rule) => rule.roleId));
                        const addable = roleOptions.filter((option) => !taken.has(option.value));
                        if (!rulesHere.length && (!editable || !addable.length)) return null;
                        return (
                          <div className="mt-2 pt-2 border-t border-[#F1F5F9] flex flex-col gap-1.5">
                            {rulesHere.map((rule) => {
                              const key = roleKey(rule.branchId, rule.roleId);
                              const savingRole = Boolean(savingRoleKeys[key]);
                              return (
                                <div key={key} className="flex items-center gap-3 pl-3">
                                  <p className="min-w-0 flex-1 text-[13px] leading-[18px] font-OpenSansRegular text-[#45556C] truncate">
                                    <span className="text-[#868DA6]">Role · </span>
                                    <span className="font-OpenSansSemiBold text-[#1A2332] capitalize">{rule.roleName}</span>
                                  </p>
                                  <div className="shrink-0 flex items-center gap-2">
                                    {savingRole ? <Loader variant="inline" ariaLabel="saving" /> : null}
                                    <PayRulesDropdown
                                      user={rule}
                                      saving={savingRole}
                                      disabled={!editable}
                                      onToggle={(patch) => patchRoleRule(rule, patch)}
                                      onReset={() => removeRoleRule(rule)}
                                      resetLabel="Remove — follow the branch"
                                      canReset
                                    />
                                  </div>
                                </div>
                              );
                            })}
                            {editable && addable.length ? (
                              <div className="pl-3 flex items-center gap-2">
                                <span className="text-[12px] leading-[18px] font-OpenSansRegular text-[#868DA6] shrink-0">
                                  Add a policy for a role:
                                </span>
                                <div className="w-[190px]">
                                  <FilterSelect
                                    label="roles"
                                    value={null}
                                    options={addable}
                                    onChange={(roleId) => {
                                      if (roleId !== null) addRoleRule(branchItem, roleId);
                                    }}
                                  />
                                </div>
                              </div>
                            ) : null}
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
            )
          ) : loading ? (
            <div className="py-10 flex justify-center">
              <Loader variant="inline" ariaLabel="loading-team" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="py-10 text-center text-[14px] font-OpenSansRegular text-[#868DA6]">
              {users.length === 0 ? "No team members found." : "No members match these filters."}
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {filtered.map((user) => {
                const roleName = (user.roles || [])[0]?.name;
                const saving = Boolean(savingIds[user.id]);
                const ownRule = hasOwnPayRule(user);
                const inheritsFrom = branchOf(user);
                const effective = effectiveOf(user);
                return (
                  <div
                    key={user.id}
                    className="bg-white rounded-[8px] border border-[#E5E8F3] px-3 py-2.5"
                  >
                    <div className="flex items-center gap-3">
                      <MemberAvatar user={user} />
                      <div className="min-w-0 flex-1">
                        <p className="text-[14px] leading-5 font-OpenSansSemiBold text-[#1A2332] truncate">
                          {user.name || user.email || "—"}
                        </p>
                        <div className="flex items-center gap-2 min-w-0">
                          {roleName ? (
                            <span className="inline-flex shrink-0 px-2 py-[1px] rounded-[6px] bg-[#EAF3FF] text-[#0058E5] text-[11px] leading-[16px] font-OpenSansSemiBold capitalize">
                              {roleName}
                            </span>
                          ) : null}
                          {/* Where the rule comes from. A person following
                              their branch and one set by hand to the same
                              rule look identical in the control, and the
                              difference decides what changes when the branch
                              rule does. */}
                          <span className="text-[11px] leading-[16px] font-OpenSansRegular text-[#868DA6] truncate">
                            {ownRule ? "Own policy" : followsLabel(user)}
                          </span>
                        </div>
                      </div>
                      <div className="shrink-0 flex items-center gap-2">
                        {saving ? <Loader variant="inline" ariaLabel="saving" /> : null}
                        <PayRulesDropdown
                          user={effective}
                          saving={saving}
                          disabled={!editable}
                          onToggle={(patch) => patchUser(user, patch)}
                          onReset={() => followBranch(user)}
                          resetLabel={
                            inheritsFrom
                              ? `Clear own policy — follow ${inheritsFrom.name}`
                              : "Clear own policy"
                          }
                          canReset={ownRule}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-[#E5E8F3] flex items-center justify-between gap-3">
          <p className="text-[12px] leading-[18px] font-OpenSansRegular text-[#868DA6]">
            Changes save as you make them.
          </p>
          <button
            type="button"
            onClick={onClose}
            className="h-9 px-4 rounded-lg border border-[#E2E8F0] text-[#1F2A44] text-[14px] font-OpenSansSemiBold"
          >
            Done
          </button>
        </div>
      </div>
    </>
  );
};

export default OtRuleDrawer;
